
import { GoogleGenAI, Type } from "@google/genai";
import { UIElement, DesignSystem } from "../types.ts";

export const extractStructure = async (base64Image: string): Promise<UIElement[]> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  const model = 'gemini-3-flash-preview';
  
  const prompt = `Analyze this UI sketch image. Extract every distinct element (buttons, text inputs, images, icons, headers, navigation, etc.). 
  Provide precise spatial coordinates and dimensions. Use a coordinate system from 0-1000 for both x, y, width, and height.
  
  Format the output as a JSON array of objects.`;

  const response = await ai.models.generateContent({
    model,
    contents: {
      parts: [
        { inlineData: { data: base64Image.split(',')[1], mimeType: 'image/png' } },
        { text: prompt }
      ]
    },
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            id: { type: Type.STRING },
            type: { type: Type.STRING, description: "Component type like button, input, text, img, etc." },
            text: { type: Type.STRING, description: "Content text if applicable" },
            x: { type: Type.NUMBER, description: "0-1000 scale" },
            y: { type: Type.NUMBER, description: "0-1000 scale" },
            width: { type: Type.NUMBER },
            height: { type: Type.NUMBER }
          },
          required: ["id", "type", "x", "y", "width", "height"]
        }
      }
    }
  });

  return JSON.parse(response.text || '[]');
};

export const generateDesignSystem = async (elements: UIElement[]): Promise<DesignSystem> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  const model = 'gemini-3-flash-preview';
  const prompt = `Based on these UI elements extracted from a sketch: ${JSON.stringify(elements)}, define a cohesive modern Design System.
  Choose professional Tailwind colors, typography, and specific UI/UX enhancements (animations, shadows, spacing).
  Return a JSON object representing the design system.`;

  const response = await ai.models.generateContent({
    model,
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          colorPalette: {
            type: Type.OBJECT,
            properties: {
              primary: { type: Type.STRING },
              secondary: { type: Type.STRING },
              background: { type: Type.STRING },
              text: { type: Type.STRING },
              accent: { type: Type.STRING }
            }
          },
          typography: {
            type: Type.OBJECT,
            properties: {
              fontFamily: { type: Type.STRING },
              baseSize: { type: Type.STRING }
            }
          },
          effects: {
            type: Type.OBJECT,
            properties: {
              borderRadius: { type: Type.STRING },
              shadow: { type: Type.STRING },
              animations: { type: Type.ARRAY, items: { type: Type.STRING } }
            }
          }
        }
      }
    }
  });

  return JSON.parse(response.text || '{}');
};

export const generateCode = async (elements: UIElement[], design: DesignSystem): Promise<string> => {
  const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
  const model = 'gemini-3-flash-preview';
  const prompt = `Merge the structural layout and design system provided to generate a single-file production-ready HTML with Tailwind CSS.
  
  STRUCTURE: ${JSON.stringify(elements)}
  DESIGN: ${JSON.stringify(design)}
  
  Rules:
  1. Use Tailwind CSS CDN.
  2. The layout must be 1:1 identical to the coordinates provided. Use absolute positioning within a relative container or advanced Flex/Grid layouts to match the intent.
  3. Include smooth transitions and micro-interactions.
  4. Ensure font consistency with the design system.
  5. Return ONLY the full HTML code.`;

  const response = await ai.models.generateContent({
    model,
    contents: prompt
  });

  return response.text || '';
};
